'use client';

import type { ReactNode } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { BrainIcon, Cancel01Icon } from '@terra/ui-icons';
import { VStack, HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Button } from '../../../../../components/styled/button';
import { Text } from '../../../../../components/styled/text';
import { LegacyDialog } from '../../../../../components/dialog';
import { IconButton } from '../../../../../components/icon-button';
import type { EditModalProps } from '../../tool-config-types';
import type { MemoryData } from './types';
import { MemoryEditForm } from './memory-edit-form';

export const MemoryEditModal = ({
  isOpen,
  onClose,
  onSubmit,
  extractedData,
}: EditModalProps) => {
  const t = useTranslations('designSystem.chat.editMemory');
  const initialMemory: MemoryData = useMemo(
    () =>
      extractedData && extractedData.length > 0
        ? (extractedData[0] as MemoryData)
        : { content: '' },
    [extractedData],
  );
  const [memory, setMemory] = useState<MemoryData>(initialMemory);
  const handleSubmit = useCallback(() => {
    onSubmit({
      content: memory.content,
      ...(memory.category && { category: memory.category }),
    });
    onClose();
  }, [memory, onSubmit, onClose]);
  const isValid = memory.content.trim().length > 0;

  if (!isOpen) return null;

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="500px">
          <VStack gap={3} padding={4} width="100%">
            <HStack
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              flexShrink={0}
            >
              <HStack gap={2} alignItems="center">
                <BrainIcon size={18} color="var(--colors-purple-9)" />
                <Text fontSize="md" fontWeight="bold">
                  {t('title')}
                </Text>
              </HStack>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton variant="ghost" size="xs" aria-label="Close">
                  <Cancel01Icon size={14} />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>

            <MemoryEditForm memory={memory} onChange={setMemory} />

            <HStack
              justifyContent="flex-end"
              gap={2}
              width="100%"
              flexShrink={0}
            >
              <Button variant="outline" onClick={onClose}>
                {t('cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={!isValid}>
                {t('save')}
              </Button>
            </HStack>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
};

export const renderMemoryEditModal = (props: EditModalProps): ReactNode => (
  <MemoryEditModal {...props} />
);
